import {FC, useEffect, useState} from 'react'
import {dispatch} from 'app/store'
import classNames from 'classnames'
import {isEqual} from 'lodash'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'
import {useMutation} from '@tanstack/react-query'

import {LoadingSpinner, RenderIf} from 'components/Basic'
import {ActionContainer} from 'components/Layouts'
import {colorCodes, trailingSlashRegEx} from 'definitions/constant'
import {selectSettingsUsers, settingsActions} from 'features/Settings'
import {useActions, usePolicy, useUsersImported} from 'hooks'
import {SettingsService} from 'services'
import Toast from 'components/Basic/Toast'
import {
  DefaultBlockPagePayload,
  SliceStatus,
  UpdateCustomBlockPages,
  UpdateEndpointsConfigAction,
  SettingsRoutesEnum,
} from 'interfaces'
import {userActions} from '../../features/User/userSlice'
import {Button} from 'design'
import {useEndpointSettingsQuery} from 'features/Settings/Endpoints/useEndpointSettingsQuery'
import {useEndpointConfigSnapshot} from 'hooks/snapshots/useEndpointConfigSnapshot'
import {CanIAccess, PERMISSIONS} from 'rbac'

export const SettingsFooter: FC = () => {
  const {
    updateDefaultBlockPagesStatus,
    blockPages,
    endpointsConfig,
    defaultBlockPagePayload,
    status,
    updateCustomBlockPageStatus,
    savePressed,
    formSubmitted,
    updatedSettingField,
  } = useSelector(selectSettingsUsers)
  const {lastUpdated} = useUsersImported()

  const {pathname} = usePolicy()

  const endpointsEnabledRoutes = [
    SettingsRoutesEnum.ENDPOINTS,
    SettingsRoutesEnum.USERS,
    SettingsRoutesEnum.BLOCK_PAGES,
  ]

  const isEndpointsSettingsEnabled = endpointsEnabledRoutes.includes(
    pathname.replace(trailingSlashRegEx, '') as SettingsRoutesEnum,
  )

  const {endpointsConfigData} = useEndpointSettingsQuery(
    isEndpointsSettingsEnabled,
  )
  const {
    endpointConfigSnapshot,
    setEndpointConfigSnapshot,
    resetToEndpointConfigSnapshot,
  } = useEndpointConfigSnapshot()
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const {isSnackbarActionAvailable} = useActions()
  const {t} = useTranslation(['common', 'settings'])

  const pendingState =
    updateDefaultBlockPagesStatus === SliceStatus.pending ||
    status === SliceStatus.pending ||
    updateCustomBlockPageStatus === SliceStatus.pending ||
    isSaving

  const isEndpointConfigFormValid = endpointsConfig?.isFormValid
  const isSettingsUsersPage = pathname.includes(SettingsRoutesEnum.USERS)

  const onCancel = () => {
    switch (pathname.replace(trailingSlashRegEx, '')) {
      case SettingsRoutesEnum.ENDPOINTS: {
        resetToEndpointConfigSnapshot()
        break
      }
      case SettingsRoutesEnum.BLOCK_PAGES:
        dispatch(settingsActions.removeBlockPagesSnapshot())
        dispatch(
          settingsActions.updateDefaultBlockPagePayload(
            {} as DefaultBlockPagePayload,
          ),
        )
        break
      case SettingsRoutesEnum.USERS: {
        resetToEndpointConfigSnapshot()
        dispatch(settingsActions.setShowEndpointConfigView(false))
        break
      }
    }
  }

  const saveChanges = () => {
    if (pathname.includes(SettingsRoutesEnum.BLOCK_PAGES)) {
      dispatch(settingsActions.formSubmitted(false))
      dispatch(settingsActions.savePressed(false))
      if (defaultBlockPagePayload.defaultPages?.length) {
        dispatch(
          settingsActions.defaultBlockPagesUpdate(defaultBlockPagePayload),
        )
      } else if (
        !isEqual(blockPages.data.pages.custom, blockPages.snapshot.pages.custom)
      ) {
        const updateCustomBlockPagePayload: UpdateCustomBlockPages = {
          addCustomPages: [],
          updateCustomPages: [],
        }
        blockPages.data.pages.custom.forEach(ele => {
          if (!ele.pageId) {
            updateCustomBlockPagePayload.addCustomPages?.push({
              label: ele.label,
              type: ele.type,
              html: ele?.html || '',
            })
          } else {
            const index = blockPages.updatedData.addCustomPages?.findIndex(
              item => item.label === ele.label,
            )
            if (index !== undefined && index >= 0) {
              const objToPush = {
                pageId: ele.pageId,
                type: ele.type,
                html: ele?.html || undefined,
                label: ele.label,
              }
              updateCustomBlockPagePayload.updateCustomPages?.push(objToPush)
            }
          }
        })
        if (
          blockPages.updatedData.deleteCustomPages &&
          blockPages.updatedData.deleteCustomPages?.length > 0
        ) {
          updateCustomBlockPagePayload.deleteCustomPages =
            blockPages.updatedData.deleteCustomPages
        }
        dispatch(
          settingsActions.customBlockPagesUpdate(updateCustomBlockPagePayload),
        )
        dispatch(settingsActions.resetSettingUpdateField())
      }
    }
  }

  const handleEndpointSettingsUpdate = async (
    payload: UpdateEndpointsConfigAction,
  ) => {
    try {
      setIsSaving(true)
      const {status} = await SettingsService.updateEndpointsConfig(payload)
      if (status === 200 && endpointConfigSnapshot) {
        setEndpointConfigSnapshot({
          ...endpointConfigSnapshot,
          ...payload,
        })
      }
    } catch (_error) {
      Toast({
        message: t('settings:ENDPOINT_SETTINGS_UPDATE_FAILED'),
        type: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const updateEndpointConfig = useMutation(handleEndpointSettingsUpdate, {
    onSuccess: () => {
      Toast({
        message: t('settings:ENDPOINT_SETTINGS_UPDATE_SUCCESS'),
        type: 'success',
      })
    },
  })

  const onSaveChanges = () => {
    if (pathname.includes(SettingsRoutesEnum.ENDPOINTS)) {
      const payload = {
        antiTamperPassword: endpointsConfigData.antiTamperPassword,
        failClosed: endpointsConfigData.failClosed,
      }
      updateEndpointConfig.mutate(payload)

      // This dispatch action is temporary and will be removed when import status is part of login api response
      dispatch(
        userActions.setUpdatedAt(
          endpointsConfigData.requireDelegatedAuth ? lastUpdated ?? '' : '',
        ),
      )
    } else if (isSettingsUsersPage) {
      const payload = {...endpointsConfigData}
      updateEndpointConfig.mutate(payload)
    } else if (pathname.includes(SettingsRoutesEnum.BLOCK_PAGES)) {
      if (updatedSettingField.value) {
        dispatch(settingsActions.savePressed(true))
        document.getElementById('submit-custom-block-page')?.click()
      } else saveChanges()
    }
  }

  useEffect(() => {
    if (formSubmitted && savePressed) saveChanges()
  }, [formSubmitted, savePressed])

  return (
    <CanIAccess reqPermissions={[PERMISSIONS.SETTINGS.WRITE]}>
      <ActionContainer
        className={classNames(
          'border-t-0.5 border-gray-400 flex justify-end items-center bg-dope-black',
          {
            hidden: !isSnackbarActionAvailable,
          },
        )}
      >
        <p
          className="text-white karla-bold text-sm mr-6"
          data-cy="setting-confirm-footer"
          dangerouslySetInnerHTML={{
            __html: t('common:SAVE_FOOTER_MESSAGE'),
          }}
        ></p>
        <Button
          className="mr-3"
          size="md"
          borderRadius="rounded"
          colorScheme="red"
          onClick={onCancel}
          data-cy="setting-cancel-button"
        >
          {t('common:CANCEL')}
        </Button>

        <Button
          size="sm"
          className="mr-6"
          borderRadius="rounded"
          data-cy="save-button-settings"
          onClick={onSaveChanges}
          disabled={
            pendingState || (isSettingsUsersPage && !isEndpointConfigFormValid)
          }
        >
          <RenderIf isTrue={!pendingState}>{t('common:SAVE')}</RenderIf>
          <RenderIf isTrue={pendingState}>
            <LoadingSpinner
              type="TailSpin"
              color={colorCodes.dopeBlack}
              width={16}
              height={16}
            />
          </RenderIf>
        </Button>
      </ActionContainer>
    </CanIAccess>
  )
}
